'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FieldType {
  id: string;
  name: string;
  icon: string;
  category: 'basic' | 'advanced' | 'layout';
  description: string;
}

interface FieldPaletteProps {
  onFieldSelect: (field: FieldType) => void;
}

const FieldPalette = ({ onFieldSelect }: FieldPaletteProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const fieldTypes: FieldType[] = [
    {
      id: 'text',
      name: 'Texto Simples',
      icon: 'DocumentTextIcon',
      category: 'basic',
      description: 'Campo de entrada de texto de linha única',
    },
    {
      id: 'textarea',
      name: 'Área de Texto',
      icon: 'DocumentIcon',
      category: 'basic',
      description: 'Campo de entrada de texto multilinha',
    },
    {
      id: 'number',
      name: 'Número',
      icon: 'HashtagIcon',
      category: 'basic',
      description: 'Campo numérico com validação',
    },
    {
      id: 'email',
      name: 'E-mail',
      icon: 'EnvelopeIcon',
      category: 'basic',
      description: 'Campo de e-mail com validação',
    },
    {
      id: 'phone',
      name: 'Telefone',
      icon: 'PhoneIcon',
      category: 'basic',
      description: 'Campo de telefone com máscara',
    },
    {
      id: 'select',
      name: 'Seleção Única',
      icon: 'ListBulletIcon',
      category: 'basic',
      description: 'Menu suspenso com opções',
    },
    {
      id: 'multiselect',
      name: 'Seleção Múltipla',
      icon: 'QueueListIcon',
      category: 'advanced',
      description: 'Seleção de múltiplas opções',
    },
    {
      id: 'radio',
      name: 'Botões de Rádio',
      icon: 'CircleStackIcon',
      category: 'basic',
      description: 'Grupo de opções exclusivas',
    },
    {
      id: 'checkbox',
      name: 'Caixas de Seleção',
      icon: 'CheckCircleIcon',
      category: 'basic',
      description: 'Múltiplas opções independentes',
    },
    {
      id: 'date',
      name: 'Data',
      icon: 'CalendarIcon',
      category: 'basic',
      description: 'Seletor de data',
    },
    {
      id: 'datetime',
      name: 'Data e Hora',
      icon: 'ClockIcon',
      category: 'advanced',
      description: 'Seletor de data e hora',
    },
    {
      id: 'file',
      name: 'Upload de Arquivo',
      icon: 'ArrowUpTrayIcon',
      category: 'advanced',
      description: 'Upload de documentos e imagens',
    },
    {
      id: 'richtext',
      name: 'Editor de Texto Rico',
      icon: 'PencilSquareIcon',
      category: 'advanced',
      description: 'Editor WYSIWYG completo',
    },
    {
      id: 'section',
      name: 'Seção',
      icon: 'RectangleGroupIcon',
      category: 'layout',
      description: 'Agrupamento visual de campos',
    },
    {
      id: 'divider',
      name: 'Divisor',
      icon: 'MinusIcon',
      category: 'layout',
      description: 'Linha divisória horizontal',
    },
    {
      id: 'html',
      name: 'HTML Personalizado',
      icon: 'CodeBracketIcon',
      category: 'advanced',
      description: 'Conteúdo HTML customizado',
    },
  ];

  const categories = [
    { id: 'all', name: 'Todos', icon: 'Squares2X2Icon' },
    { id: 'basic', name: 'Básicos', icon: 'CubeIcon' },
    { id: 'advanced', name: 'Avançados', icon: 'SparklesIcon' },
    { id: 'layout', name: 'Layout', icon: 'RectangleStackIcon' },
  ];

  const filteredFields = fieldTypes.filter((field) => {
    const matchesSearch = field.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'all' || field.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:151:4" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="151" data-component-file="FieldPalette.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-full%20flex%20flex-col%20bg-surface%20border-r%20border-border%22%7D" className="h-full flex flex-col bg-surface border-r border-border">
      <div data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:152:6" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="152" data-component-file="FieldPalette.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%20border-b%20border-border%22%7D" className="p-4 border-b border-border">
        <h2 data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:153:8" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="153" data-component-file="FieldPalette.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-foreground%20mb-4%22%2C%22textContent%22%3A%22Paleta%20de%20Campos%22%7D" className="text-lg font-semibold text-foreground mb-4">Paleta de Campos</h2>
        
        <div data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:155:8" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="155" data-component-file="FieldPalette.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20mb-4%22%7D" className="relative mb-4">
          <Icon data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:156:10" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="156" data-component-file="FieldPalette.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22absolute%20left-3%20top-1%2F2%20-translate-y-1%2F2%20text-muted-foreground%22%7D"
            name="MagnifyingGlassIcon"
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:161:10" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="161" data-component-file="FieldPalette.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AsearchQuery%5D%22%2C%22className%22%3A%22w-full%20pl-10%20pr-4%20py-2%20bg-muted%20border%20border-border%20rounded-md%20text-sm%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-ring%22%7D"
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar campos..."
            className="w-full pl-10 pr-4 py-2 bg-muted border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:170:8" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="170" data-component-file="FieldPalette.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-2%20overflow-x-auto%20pb-2%22%7D" className="flex gap-2 overflow-x-auto pb-2">
          {categories.map((category) => (
            <button data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:172:12" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="172" data-component-file="FieldPalette.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={category.id}
              onClick={() => setActiveCategory(category.id)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-micro ${
                activeCategory === category.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <Icon data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:181:14" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="181" data-component-file="FieldPalette.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={category.icon as any} size={14} />
              {category.name}
            </button>
          ))}
        </div>
      </div>

      <div data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:188:6" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="188" data-component-file="FieldPalette.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20overflow-y-auto%20p-4%22%7D" className="flex-1 overflow-y-auto p-4">
        <div data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:189:8" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="189" data-component-file="FieldPalette.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-2%22%7D" className="space-y-2">
          {filteredFields.map((field) => (
            <button data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:191:12" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="191" data-component-file="FieldPalette.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20p-3%20bg-card%20border%20border-border%20rounded-md%20hover%3Aborder-accent%20hover%3Ashadow-subtle%20transition-micro%20text-left%20group%22%7D"
              key={field.id}
              onClick={() => onFieldSelect(field)}
              className="w-full p-3 bg-card border border-border rounded-md hover:border-accent hover:shadow-subtle transition-micro text-left group"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('fieldType', JSON.stringify(field));
              }}
            >
              <div data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:200:14" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="200" data-component-file="FieldPalette.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-3%22%7D" className="flex items-start gap-3">
                <div data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:201:16" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="201" data-component-file="FieldPalette.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%20w-8%20h-8%20bg-accent%2F10%20rounded-md%20flex%20items-center%20justify-center%20group-hover%3Abg-accent%2F20%20transition-micro%22%7D" className="flex-shrink-0 w-8 h-8 bg-accent/10 rounded-md flex items-center justify-center group-hover:bg-accent/20 transition-micro">
                  <Icon data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:202:18" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="202" data-component-file="FieldPalette.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22text-accent%22%7D" name={field.icon as any} size={18} className="text-accent" />
                </div>
                <div data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:204:16" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="204" data-component-file="FieldPalette.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20min-w-0%22%7D" className="flex-1 min-w-0">
                  <h3 data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:205:18" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="205" data-component-file="FieldPalette.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-sm%20font-medium%20text-foreground%20mb-1%22%7D" className="text-sm font-medium text-foreground mb-1">{field.name}</h3>
                  <p data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:206:18" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="206" data-component-file="FieldPalette.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%20line-clamp-2%22%7D" className="text-xs text-muted-foreground line-clamp-2">{field.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {filteredFields.length === 0 && (
          <div data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:214:10" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="214" data-component-file="FieldPalette.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-12%22%7D" className="text-center py-12">
            <Icon data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:215:12" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="215" data-component-file="FieldPalette.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22mx-auto%20mb-3%20text-muted-foreground%20opacity-50%22%7D" name="MagnifyingGlassIcon" size={48} className="mx-auto mb-3 text-muted-foreground opacity-50" />
            <p data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:216:12" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="216" data-component-file="FieldPalette.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%2C%22textContent%22%3A%22Nenhum%20campo%20encontrado%22%7D" className="text-sm text-muted-foreground">Nenhum campo encontrado</p>
          </div>
        )}
      </div>

      <div data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:221:6" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="221" data-component-file="FieldPalette.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%20border-t%20border-border%20bg-muted%2F50%22%7D" className="p-4 border-t border-border bg-muted/50">
        <div data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:222:8" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="222" data-component-file="FieldPalette.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20gap-2%20text-xs%20text-muted-foreground%22%7D" className="flex items-start gap-2 text-xs text-muted-foreground">
          <Icon data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:223:10" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="223" data-component-file="FieldPalette.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22InformationCircleIcon%22%2C%22className%22%3A%22flex-shrink-0%20mt-0.5%22%7D" name="InformationCircleIcon" size={16} className="flex-shrink-0 mt-0.5" />
          <p data-component-id="src/app/dynamic-form-builder/components/FieldPalette.tsx:224:10" data-component-path="src/app/dynamic-form-builder/components/FieldPalette.tsx" data-component-line="224" data-component-file="FieldPalette.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22textContent%22%3A%22Arraste%20os%20campos%20para%20o%20canvas%20ou%20clique%20para%20adicionar%20ao%20final%20do%20formul%C3%A1rio%22%7D">Arraste os campos para o canvas ou clique para adicionar ao final do formulário</p>
        </div>
      </div>
    </div>
  );
};

export default FieldPalette;